import React, { useState, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { createReservation, getNextThursdays, RESERVE_URL } from "../lib/api";
import { useT } from "../i18n/LanguageContext";

export const ReservationModal = ({ open, onClose }) => {
  const { t, lang } = useT();
  const x = t.modal;
  const thursdays = useMemo(() => {
    // Re-compute locale labels per language
    const out = [];
    const d = new Date();
    const dayDiff = (4 - d.getDay() + 7) % 7;
    d.setDate(d.getDate() + (dayDiff === 0 ? 0 : dayDiff));
    const localeMap = { it: "it-IT", en: "en-GB", es: "es-ES", fr: "fr-FR", de: "de-DE" };
    const locale = localeMap[lang] || "it-IT";
    for (let i = 0; i < 8; i++) {
      const dd = new Date(d);
      dd.setDate(dd.getDate() + i * 7);
      const iso = dd.toISOString().slice(0, 10);
      const label = dd.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" });
      out.push({ value: iso, label: label.charAt(0).toUpperCase() + label.slice(1) });
    }
    return out;
  }, [lang]);

  const [form, setForm] = useState({
    nome: "", telefono: "", persone: 2,
    data_giovedi: thursdays[0]?.value || "", note: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  // Reset data when language changes (rebuild thursdays)
  React.useEffect(() => {
    setForm((prev) => ({ ...prev, data_giovedi: prev.data_giovedi || thursdays[0]?.value || "" }));
  }, [thursdays]);

  const updateField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.nome.trim().length < 2 || form.telefono.trim().length < 6) {
      setError(x.err);
      return;
    }
    setLoading(true);
    try {
      await createReservation({
        nome: form.nome, telefono: form.telefono,
        persone: Number(form.persone),
        data_giovedi: form.data_giovedi, note: form.note,
      });
      setSuccess(true);
    } catch (err) {
      setError(x.genErr);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setSuccess(false); setError("");
    setForm({ nome: "", telefono: "", persone: 2, data_giovedi: thursdays[0]?.value || "", note: "" });
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-6"
          data-testid="reservation-modal"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => { onClose(); setTimeout(reset, 400); }} />
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 0.61, 0.36, 1] }}
            className="modal-glass relative w-full md:max-w-lg rounded-t-3xl md:rounded-2xl overflow-hidden"
          >
            <button onClick={() => { onClose(); setTimeout(reset, 400); }}
              aria-label="Close"
              className="absolute top-4 right-4 z-10 text-[var(--pn-cream)]/70 hover:text-[var(--pn-amber)] transition-colors"
              data-testid="modal-close"
            ><X size={22} /></button>

            {!success ? (
              <div className="p-6 md:p-9">
                <span className="eyebrow">{x.eyebrow}</span>
                <h3 className="mt-3 font-display font-light text-[28px] md:text-[34px] leading-[1.1] text-[var(--pn-milk)]">
                  {x.title_a}<em className="italic text-[var(--pn-amber)]">{x.title_b}</em>
                </h3>
                <p className="mt-3 text-[14px] text-[var(--pn-cream)]/65 font-light leading-relaxed">{x.sub}</p>

                <form onSubmit={handleSubmit} className="mt-7 space-y-5">
                  <div>
                    <label className="pn-label" htmlFor="nome">{x.l_name}</label>
                    <input id="nome" type="text" className="pn-input" placeholder={x.ph_name}
                      value={form.nome} onChange={(e) => updateField("nome", e.target.value)}
                      data-testid="input-nome" required />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="pn-label" htmlFor="telefono">{x.l_phone}</label>
                      <input id="telefono" type="tel" className="pn-input" placeholder={x.ph_phone}
                        value={form.telefono} onChange={(e) => updateField("telefono", e.target.value)}
                        data-testid="input-telefono" required />
                    </div>
                    <div>
                      <label className="pn-label" htmlFor="persone">{x.l_people}</label>
                      <input id="persone" type="number" min="1" max="30" className="pn-input"
                        value={form.persone} onChange={(e) => updateField("persone", e.target.value)}
                        data-testid="input-persone" required />
                    </div>
                  </div>
                  <div>
                    <label className="pn-label" htmlFor="giovedi">{x.l_thu}</label>
                    <select id="giovedi" className="pn-input"
                      value={form.data_giovedi} onChange={(e) => updateField("data_giovedi", e.target.value)}
                      data-testid="input-data"
                    >
                      {thursdays.map((t2) => (
                        <option key={t2.value} value={t2.value} className="bg-[#1A1410]">{t2.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="pn-label" htmlFor="note">{x.l_note}</label>
                    <textarea id="note" rows="2" className="pn-input resize-none" placeholder={x.ph_note}
                      value={form.note} onChange={(e) => updateField("note", e.target.value)}
                      data-testid="input-note" />
                  </div>

                  {error && <p className="text-[13px] text-[#E07A5F]" data-testid="form-error">{error}</p>}

                  <button type="submit" disabled={loading}
                    className="cta-primary w-full justify-center !text-[15px] disabled:opacity-60"
                    data-testid="submit-reservation"
                  >
                    {loading ? x.submitting : x.submit}
                    {!loading && <span aria-hidden>→</span>}
                  </button>

                  <p className="text-center text-[12px] text-[var(--pn-cream)]/45 mt-3">
                    {x.portalHint}{" "}
                    <a href={RESERVE_URL} target="_blank" rel="noopener noreferrer"
                      className="text-[var(--pn-amber)] hover:underline">{x.portalLink}</a>
                  </p>
                </form>
              </div>
            ) : (
              <div className="p-9 text-center" data-testid="reservation-success">
                <div className="mx-auto w-16 h-16 rounded-full border border-[var(--pn-amber)]/50 flex items-center justify-center text-[var(--pn-amber)] text-2xl">✓</div>
                <h3 className="mt-6 font-display font-light text-[28px] text-[var(--pn-milk)]">
                  {x.successTitle_a}<em className="italic text-[var(--pn-amber)]">{x.successTitle_b}</em>
                </h3>
                <p className="mt-4 text-[15px] text-[var(--pn-cream)]/75 leading-relaxed">{x.successBody}</p>
                <button onClick={() => { onClose(); setTimeout(reset, 400); }} className="cta-secondary mt-8" data-testid="close-success">{x.close}</button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
